import React from 'react';
import clsx from 'clsx';
import { MenuItem, Select, styled, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid-premium';

import { type SelectFieldProps } from 'app/main/components/fields/SelectField';
import type { EditInputCellParams } from '../DetailsSectionTableInputCells';

type SelectEditInputCellParams = EditInputCellParams & Pick<SelectFieldProps, 'options' | 'disableEmptyOption'>;

const SelectEditInputCell = styled((props: SelectEditInputCellParams) => {
	const apiRef = useGridApiContext();

	const inputRef = React.useRef<HTMLInputElement>(undefined);

	const baseParseValue = newValue => ({ [props.field]: newValue.value });

	const handleChange = async (event: SelectChangeEvent) => {
		const newValue =
			event.target.value !== '' ? props.options.find(option => option.value === event.target.value) : undefined;
		const newRow = (props.parseValue || baseParseValue)(newValue || {});
		Object.keys(newRow).forEach(async key =>
			apiRef.current.setEditCellValue({ id: props.id, field: key, value: newRow[key] })
		);
		apiRef.current.updateRows([{ ...props.row, ...newRow }]);
	};

	const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = async event => {
		if (event.key === 'Enter') {
			event.preventDefault();
			event.stopPropagation();
		}
	};

	React.useEffect(() => {
		if (props.hasFocus) inputRef.current?.focus();
	}, [props.hasFocus]);

	return (
		<Select
			className={clsx('h-full rounded-none border-none', props.className)}
			slotProps={{ input: { className: 'p-8 pr-0' } }}
			inputRef={inputRef}
			value={props.value || ''}
			variant="filled"
			size="small"
			fullWidth
			disableUnderline
			onChange={handleChange}
			onKeyDown={handleKeyDown}
		>
			{!props.disableEmptyOption && <MenuItem value="">-</MenuItem>}
			{props.options.map(option => (
				<MenuItem key={option.value} className="flex flex-col items-start" value={option.value}>
					<Typography className="font-400" variant="subtitle2" color="text.primary">
						{option.title}
					</Typography>
					{option.description && (
						<Typography className="font-400" variant="subtitle2" color="text.secondary">
							{option.description}
						</Typography>
					)}
				</MenuItem>
			))}
		</Select>
	);
})(({ theme }) => ({
	'& > .MuiOutlinedInput-notchedOutline': {
		border: 0
	}
}));

export default React.memo(SelectEditInputCell);
